import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import FormControl from '@mui/material/FormControl'

import FormLabel from '@mui/material/FormLabel'

import MDEditor from '@uiw/react-md-editor'

import TagSelector from '../tags/TagSelector'
import TerminologySourcesEditor, { emptySource, validateSources } from '../shared/TerminologySourcesEditor'
import { AdminTerminologyModalStyle } from '../styles'
import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'

import { setErrorNotification } from '../../reducers/notificationReducer'
import { deleteTerminology, updateTerminology } from '../../reducers/terminologyReducer'

const hydrateSources = (existing) => {
    if (!existing || existing.length === 0) return [emptySource()]
    return existing.map(s => ({
        source: s.source || '',
        url: s.url || '',
        publication_data: s.publication_data || '',
        pub_date: s.pub_date || '',
    }))
}

const AdminTerminologyModal = (props) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    // form state
    const [term, setTerm] = useState('')
    const [termError, setTermError] = useState(false)
    const [termHelper, setTermHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [sources, setSources] = useState([emptySource()])
    const [tags, setTags] = useState([])


    // init fields
    useEffect(() => {
        setTerm(props.term)
        setDescription(props.description)
        setSources(hydrateSources(props.sources))
        setTags((props.tags || []).map(String))
    }, [props])


    // basic form validation
    const validate = (field, setError, setHelper) => {
        if(!field || field.length === 0) {
            setError(true)
            setHelper('This field is required')
            return false
        } else {
            setError(false)
            setHelper('')
            return true
        }
    }

    const cleanSources = () => sources.map(s => ({
        source: s.source,
        url: s.url || null,
        publication_data: s.publication_data || null,
        pub_date: s.pub_date || null,
    }))

    const buildData = (extra = {}) => ({
        term,
        description,
        tags,
        sources: cleanSources(),
        ...extra,
    })

    const validateAll = () => {
        const termOk = validate(term, setTermError, setTermHelper)
        const descOk = validate(description, setDescriptionError, setDescriptionHelper)
        const sourcesCheck = validateSources(sources)
        if (!sourcesCheck.ok) {
            dispatch(setErrorNotification(sourcesCheck.message))
            return false
        }
        if (!termOk || !descOk) {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
            return false
        }
        return true
    }

    // toggle approve state
    const handleToggleApprove = () => {
        if (!validateAll()) return
        if (window.confirm('Are you sure? Note any changes you have made will be included.')) {
            const data = buildData({ approved: !props.approved })
            dispatch(updateTerminology(props.id, data, user.staff || user.terminology_admin))
            handleClose()
            if (props.onToggleApprove) props.onToggleApprove({ ...props, ...data })
        }
    }

    // do not change approved state just update fields
    const handleSave = () => {
        if (!validateAll()) return
        const data = buildData()
        dispatch(updateTerminology(props.id, data, user.staff || user.terminology_admin))
    }

    const [open, setOpen] = useState(false)

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    const handleDelete = () => {
        if (window.confirm('Are you sure you want to delete this terminology? This action cannot be undone.')) {
            dispatch(deleteTerminology(props.id, user.staff || user.terminology_admin))
            handleClose()
            if (props.onDelete) props.onDelete()
        }
    }

    return (
        <div>
            <IconButton {...AdminTerminologyModalStyle.icon.props} sx={AdminTerminologyModalStyle.icon.style} onClick={()=>handleClickOpen()}>
                <EditIcon />
            </IconButton>
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby={`admin-terminology-modal-${props.id}`}
                open={open}
                fullWidth
                maxWidth="md"
            >
                <BootstrapDialogTitle id={`admin-terminology-modal-${props.id}`} onClose={()=>handleClose()}>
                    <Box sx={AdminTerminologyModalStyle.headingContainer}>
                        <MenuBookIcon sx={AdminTerminologyModalStyle.icon}/>
                        Edit Glossary
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                value={term}
                                onChange={e => setTerm(e.target.value)}
                                label="Term"
                                required
                                error={termError}
                                helperText={termHelper}
                                onBlur={()=>validate(term, setTermError, setTermHelper)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <FormLabel component="legend">Description (Markdown supported)</FormLabel>
                                <Box sx={{ marginTop: '10px' }}>
                                    <MDEditor
                                        value={description}
                                        onChange={setDescription}
                                        preview="edit"
                                        hideToolbar={false}
                                        data-color-mode="light"
                                    />
                                </Box>
                                {descriptionError && (
                                    <Typography color="error" variant="caption">
                                        {descriptionHelper}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TerminologySourcesEditor sources={sources} onChange={setSources} />
                        </Grid>
                        <Grid item xs={12}>
                            <Box>
                                <TagSelector
                                    tagType="default"
                                    selectedTags={tags}
                                    onChange={setTags}
                                    multiple={true}
                                    label="Tags"
                                    maxTags={5}
                                />
                            </Box>
                        </Grid>

                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDelete} sx={{ color: 'red' }}>
                        Delete
                    </Button>
                    <Button onClick={handleSave}>
                        Save Changes
                    </Button>
                    <Button onClick={handleToggleApprove} variant="contained">
                        {props.approved ? 'Unapprove' : 'Approve'}
                    </Button>
                    <Button onClick={handleClose}>
                        Close
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default AdminTerminologyModal
